/**
 * Copyright (c) 2025 Tj L Collins, DaddySavesTheWorld, and Mythbound Adventure Studio
 * All Rights Reserved
 *
 * This file is part of Mythbound Adventure Studio, a proprietary software project.
 * Unauthorized copying, modification, distribution, or use of this software,
 * via any medium, is strictly prohibited without explicit written permission
 * from the copyright holders.
 *
 * For licensing inquiries, contact: taleweavertik@gmail.com
 */

/**
 * App.tsx
 *
 * Purpose: Root router and application shell for Mythbound.
 * Created: 2025-01-19
 * Modified: 2026-02-26
 * Contract: shared/contracts/auth/v1/oauth-error.yaml
 *
 * WHO: Frontend application bootstrap and routing logic.
 * WHAT: Defines routes, auth gates, and layout integration.
 * WHERE: Mythbound.App/src/App.tsx.
 * WHY: Centralizes routing and app lifecycle behaviors.
 */

import React, { useEffect, useState } from 'react';
import { Flex, Spinner, Text } from '@chakra-ui/react';
import { BrowserRouter as Router, Routes, Route, Navigate, useParams } from 'react-router';
import { ThemeProvider } from './theme/ThemeProvider';
import { Layout } from './components/ui/Layout';
import OAuthDebugger from './components/OAuthDebugger';
import Dashboard from './pages/Dashboard';
import Login from './pages/Login';
import AuthError from './pages/AuthError';
// OAuthCallback removed - backend handles OAuth flow directly
import SessionView from './pages/SessionView';
// Added 2026-03-11: SessionsForum page for /session/:id/forum route
import { SessionsForum } from './components/session/SessionsForum';
import CampaignBuilder from './pages/CampaignBuilder';
// Updated 2026-09-21 (PRD001A 001A.4 Wave 3): `/character/:id` now routes to
// the real data-fetching page. The old import (`./pages/CharacterSheet`'s
// default export) was a presentational component requiring a `character`
// prop that nothing here supplied — it crashed on render (001A.1 inventory
// finding). `CharacterSheetPage` fetches by route param and was previously
// unreachable dead code from this file.
import CharacterSheetPage from './pages/CharacterSheetPage';
import AdminDashboard from './pages/AdminDashboard';
import Profile from './pages/Profile';
import Settings from './pages/Settings';
import Terms from './pages/Terms';
import Privacy from './pages/Privacy';
import DevControls from './pages/DevControls';
import InviteLanding from './pages/InviteLanding';
import NotFound from './pages/NotFound';
// Added 2026-02-24: VoicePage wraps VoicePanel for /voice route (was NotFound)
import VoicePage from './pages/VoicePage';
// Added 2026-02-26: Campaign list and detail pages for /campaigns and /campaigns/:campaignId routes
import CampaignsPage from './pages/CampaignsPage';
import CampaignPage from './pages/CampaignPage';
import { AuthProvider, useAuth } from './context/AuthContext';
import { CampaignProvider } from './context/CampaignContext';
// Added 2026-09-21 (PRD001A 001A.4 Wave 3): mounted per-campaign below via
// CampaignRouteProviders — previously not mounted anywhere in this file at
// all (001A.1 inventory finding); the only real, routed consumer,
// CampaignPage.tsx, called useCharacter()/useSession() with no provider
// above it and crashed on render.
import { CharacterProvider } from './context/CharacterContext';
import { SessionProvider } from './context/SessionContext';
// Added (PRD001A 001A.5 bug fix): SessionRouteProvider needs to resolve the
// owning campaign for /session/:id itself — see that component below.
import { sessionService, type SessionState } from './services/api/SessionService';
import { ApiError } from './services/api/ApiError';
import { RoleType } from './config/roles';
import RoleBasedAccess from './components/auth/RoleBasedAccess';
import { CampaignRealtimeProvider } from './context/CampaignRealtimeContext';
// Added 2026-09-21 (PRD001A 001A.7a): honest unavailable/error UI instead of a
// blank page or a misleading login redirect. See 001A_FE_Inventory.md Section B
// for the crash this ErrorBoundary was added in response to.
// RouteResetErrorBoundary (not the bare ErrorBoundary) is used below so a
// crash on one page doesn't persist after navigating away from it — see that
// component's header comment.
import { RouteResetErrorBoundary } from './components/common/ErrorBoundary';
import { ServiceUnavailable } from './components/common/ServiceUnavailable';

/**
 * Main App Component
 * 
 * This is the root component of the Mythbound web application.
 * It sets up routing, authentication, and the overall layout structure.
 * 
 * The application uses:
 * - React Router for navigation
 * - Chakra UI (via ThemeProvider) for styling
 * - Custom Layout component for consistent UI structure
 * - Integration with Discord bot backend via the integration components
 */

// Protected Route Component
interface ProtectedRouteProps {
  element: React.ReactNode;
}

/**
 * ProtectedRoute — gates a route element on `AuthContext.authStatus`.
 *
 * WHO: Every protected `<Route>` below wraps its element in this.
 * WHAT: Branches on the four `AuthStatus` values (PRD001A 001A.7a):
 *   - `'loading'` → a loading placeholder (unchanged from before).
 *   - `'unavailable'` → `ServiceUnavailable` with a retry action wired to
 *     `checkSessionStatus`. This is the branch that did not exist previously —
 *     an auth-store outage used to fall through to the same `Navigate to
 *     "/login"` as an ordinary sign-out, which misrepresents "we couldn't
 *     check" as "you're signed out."
 *   - `'unauthenticated'` → redirect to `/login`, as before.
 *   - `'authenticated'` → render the protected element, as before.
 * WHERE: src/App.tsx.
 * WHY: See PRD001A 001A.7a acceptance: "block dependent actions and display
 * unavailable; do not convert 503/network failure to ... signed-out success."
 */
export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ element }) => {
  const { authStatus, checkSessionStatus } = useAuth();

  console.log('[ProtectedRoute] authStatus:', authStatus);

  if (authStatus === 'loading') {
    return <div>Loading...</div>; // Or your loading component
  }

  if (authStatus === 'unavailable') {
    return <ServiceUnavailable onRetry={() => void checkSessionStatus()} />;
  }

  return authStatus === 'authenticated' ? (
    <>{element}</>
  ) : (
    <Navigate to="/login" replace />
  );
};

/**
 * CampaignRouteProviders — mounts `CharacterProvider`/`SessionProvider` for a
 * single `/campaigns/:campaignId` route, keyed by that campaign's ID.
 *
 * WHO: The `/campaigns/:campaignId` route below, wrapping `CampaignPage`.
 * WHAT: Reads `campaignId` via `useParams` (this must render inside
 * `<Routes>`/`<Route>`, which is why it's a separate component rather than
 * computed once in `App`) and renders both providers keyed by that ID.
 * WHERE: src/App.tsx. Design follows the (now superseded and removed)
 * `App.example.tsx`'s `CampaignRouteWrapper`, adapted to read the real route
 * param instead of a hardcoded placeholder ID.
 * WHY: keying by `campaignId` means React fully unmounts the previous
 * `CharacterProvider`/`SessionProvider`/`CampaignPage` subtree and mounts a
 * fresh one whenever the campaign changes — direct navigation to a new
 * campaign URL and in-app campaign switching both go through the same
 * mount, so neither can leave the previous campaign's roster, active
 * character, session, or polling loop behind. Each provider's own unmount
 * cleanup (see `CharacterContext.tsx`/`SessionContext.tsx`) is what actually
 * cancels any in-flight fetch/poll at that moment — the `key` guarantees
 * that cleanup runs, it doesn't perform the cancellation itself.
 */
export const CampaignRouteProviders: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { campaignId } = useParams<{ campaignId: string }>();
  return (
    <CharacterProvider key={campaignId} campaignId={campaignId}>
      <SessionProvider key={campaignId} campaignId={campaignId} realtimeManaged>
        <CampaignRealtimeProvider campaignId={campaignId}>{children}</CampaignRealtimeProvider>
      </SessionProvider>
    </CharacterProvider>
  );
};

/**
 * SessionRouteProvider — resolves the owning campaign for `/session/:id` and
 * mounts `SessionProvider` keyed by that campaign, mirroring
 * `CampaignRouteProviders` above.
 *
 * WHO: The `/session/:id` route below, wrapping `SessionView`.
 * WHAT: `/session/:id` only carries a SESSION id, but `SessionProvider`
 * needs a CAMPAIGN id (its polling loop is keyed by campaign — see
 * `SessionContext.tsx`). This resolves the campaign via ONE
 * `sessionService.getSessionState(sessionId)` call — the same session
 * contract endpoint (`GET /api/sessions/{id}/state`) `SessionContext`'s own
 * polling loop calls repeatedly — then mounts `SessionProvider` keyed by the
 * resolved `campaign_id`, same keying rationale as `CampaignRouteProviders`.
 * `CharacterProvider` is deliberately NOT mounted here: `SessionView` fetches
 * its roster directly via `characterService`, not `useCharacter()`, and
 * nothing else this route renders (`SessionControls`, `DiceRoller`,
 * `NotificationBell`, `VoicePanel`, `DiceRollerModal`, `SpellbookDrawer`,
 * `ComingSoonModal` — checked individually) calls `useCharacter()` either.
 * WHERE: src/App.tsx.
 * WHY (bug fix, PRD001A 001A.5 follow-up): `SessionView.tsx` renders
 * `SessionControls`, which calls `useSession()`. This route previously
 * mounted `SessionView` with NO `SessionProvider` above it at all — the
 * Wave 1 inventory had wrongly attributed `SessionControls` to
 * `CampaignPage.tsx` instead of here, so the gap went unnoticed. Every
 * direct navigation to `/session/:id` threw on render and crashed into the
 * ErrorBoundary. `loading`/`forbidden`/`not_found`/`unavailable` are kept as
 * distinct states here (never collapsed into a blank page or a misleading
 * redirect) — same rule `CampaignPage.tsx`'s own roster guard follows.
 * WHY (R5 fix, 2026-09-22): this component's `sessionService.getSessionState`
 * call previously discarded everything except `campaign_id` before mounting
 * a bare `SessionProvider` — the release review's exact R5 finding, since
 * `SessionContext` only starts polling `startSession()` ever called, which
 * this route never does (nor should it: that creates a NEW session). Now the
 * full fetched `SessionState` is kept and passed to `SessionProvider` as
 * `sessionId`/`initialState`, which seeds `session`/`sessionState` from it
 * directly and starts polling immediately — see that provider's own header
 * comment in `SessionContext.tsx` for the initialization effect this feeds.
 * Passing the already-fetched state down (instead of letting the provider
 * re-fetch) also avoids a second identical network call for the same
 * resource. Navigating from one session to another within the SAME campaign
 * re-runs this effect (`sessionId` in the dependency array) without
 * remounting `SessionProvider` via the `campaignId` key when the campaign is
 * unchanged — but this component's `status` still returns to `'loading'` in
 * between (a different render branch entirely), which unmounts the previous
 * `SessionProvider` subtree and cleanly stops its polling before the next
 * one mounts fresh, so no stale interval or in-flight request from the old
 * session can apply its response to the new one.
 */
export const SessionRouteProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { id: sessionId } = useParams<{ id: string }>();
  const [campaignId, setCampaignId] = useState<string | null>(null);
  const [initialState, setInitialState] = useState<SessionState | null>(null);
  const [status, setStatus] = useState<'loading' | 'ready' | 'forbidden' | 'not_found' | 'unavailable' | 'error'>('loading');
  const [message, setMessage] = useState<string | null>(null);
  const [retryToken, setRetryToken] = useState(0);

  useEffect(() => {
    if (!sessionId) {
      setStatus('not_found');
      return;
    }
    let cancelled = false;
    const controller = new AbortController();
    setStatus('loading');
    setMessage(null);
    sessionService
      .getSessionState(sessionId, { signal: controller.signal })
      .then((state) => {
        if (cancelled) return;
        setCampaignId(state.campaign_id);
        setInitialState(state);
        setStatus('ready');
      })
      .catch((err) => {
        if (cancelled) return;
        if (err instanceof DOMException && err.name === 'AbortError') return; // route changed away mid-fetch
        if (err instanceof ApiError) {
          setMessage(err.message);
          if (err.kind === 'forbidden' || err.kind === 'unauthenticated') {
            setStatus('forbidden');
            return;
          }
          if (err.kind === 'not_found') {
            setStatus('not_found');
            return;
          }
          if (err.kind === 'unavailable' || err.kind === 'network') {
            setStatus('unavailable');
            return;
          }
        }
        setStatus('error');
      });

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [sessionId, retryToken]);

  if (status === 'loading') {
    return (
      <Flex minH="60vh" align="center" justify="center">
        <Spinner size="xl" color="#D9B555" />
      </Flex>
    );
  }

  if (status === 'unavailable') {
    return (
      <ServiceUnavailable
        title="Session unavailable"
        description={message ?? "We couldn't reach the session service right now."}
        onRetry={() => setRetryToken((n) => n + 1)}
      />
    );
  }

  if (status === 'not_found') {
    return <NotFound />;
  }

  if (status === 'forbidden') {
    return (
      <Flex minH="60vh" align="center" justify="center" direction="column" gap={2} textAlign="center" px={6}>
        <Text color="#C5C5C7" fontWeight="bold" fontSize="lg">Access restricted</Text>
        <Text color="#8a8a90" fontSize="sm" maxW="sm">
          {message ?? "You don't have permission to view this session."}
        </Text>
      </Flex>
    );
  }

  if (status === 'error') {
    return (
      <Flex minH="60vh" align="center" justify="center" direction="column" gap={2} textAlign="center" px={6}>
        <Text color="#C5C5C7" fontWeight="bold" fontSize="lg">Something went wrong</Text>
        <Text color="#8a8a90" fontSize="sm" maxW="sm">{message ?? 'Failed to load this session.'}</Text>
      </Flex>
    );
  }

  return (
    <SessionProvider
      key={campaignId}
      campaignId={campaignId ?? undefined}
      sessionId={sessionId}
      initialState={initialState ?? undefined}
      realtimeManaged
    >
      <CampaignRealtimeProvider campaignId={campaignId ?? undefined} expectedSessionId={sessionId}>{children}</CampaignRealtimeProvider>
    </SessionProvider>
  );
};

const App: React.FC = () => {
  return (
    <AuthProvider>
      <ThemeProvider>
        <CampaignProvider>
          <Router>
            {/* OAuth Debugger - only visible in development or with debug flag */}
            <OAuthDebugger />

            <RouteResetErrorBoundary>
            <Routes>
              {/* Public Routes */}
              <Route path="/login" element={<Login />} />
              <Route path="/auth/error" element={<AuthError />} />
              <Route path="/terms" element={<Terms />} />
              <Route path="/privacy" element={<Privacy />} />
              {/* OAuth callbacks handled directly by backend - no frontend route needed */}

              {/* Campaign Invite Landing - public route (no auth required) */}
              <Route path="/invite/:token" element={<InviteLanding />} />
              
              {/* Protected Routes - wrapped in Layout */}
              <Route 
                path="/" 
                element={
                  <ProtectedRoute 
                    element={
                      <Layout>
                        <Dashboard />
                      </Layout>
                    } 
                  />
                } 
              />
              <Route 
                path="/dashboard" 
                element={
                  <ProtectedRoute 
                    element={
                      <Layout>
                        <Dashboard />
                      </Layout>
                    } 
                  />
                } 
              />
              <Route
                path="/session/:id"
                element={
                  <ProtectedRoute
                    element={
                      <Layout>
                        {/* Bug fix (PRD001A 001A.5 follow-up): SessionView
                            renders SessionControls, which calls useSession()
                            — this route previously had no SessionProvider
                            above it at all and crashed on every direct
                            navigation. See SessionRouteProvider's header
                            comment above for the full rationale. */}
                        <SessionRouteProvider>
                          <SessionView />
                        </SessionRouteProvider>
                      </Layout>
                    }
                  />
                }
              />
              {/* Forum discussion threads — added 2026-03-11 */}
              <Route
                path="/session/:id/forum"
                element={
                  <ProtectedRoute
                    element={
                      <Layout>
                        <SessionsForum />
                      </Layout>
                    }
                  />
                }
              />
              <Route 
                path="/campaign/builder" 
                element={
                  <ProtectedRoute 
                    element={
                      <Layout>
                        <CampaignBuilder />
                      </Layout>
                    } 
                  />
                } 
              />
              {/* Campaigns List Route — added 2026-02-26: dedicated /campaigns page (was 404) */}
              <Route
                path="/campaigns"
                element={
                  <ProtectedRoute
                    element={
                      <Layout>
                        <CampaignsPage />
                      </Layout>
                    }
                  />
                }
              />

              {/* Campaign Detail Route — added 2026-02-26: full campaign page with roster/session.
                  Updated 2026-09-21 (PRD001A 001A.4 Wave 3): wrapped in
                  CampaignRouteProviders so CharacterProvider/SessionProvider
                  are mounted, keyed by campaignId — see that component's
                  header comment above. */}
              <Route
                path="/campaigns/:campaignId"
                element={
                  <ProtectedRoute
                    element={
                      <Layout>
                        <CampaignRouteProviders>
                          <CampaignPage />
                        </CampaignRouteProviders>
                      </Layout>
                    }
                  />
                }
              />

              {/* Alias redirect — added 2026-02-26: /campaign/create → /campaign/builder for legacy nav calls */}
              <Route path="/campaign/create" element={<Navigate to="/campaign/builder" replace />} />

              <Route
                path="/character/:id"
                element={
                  <ProtectedRoute
                    element={
                      <Layout>
                        <CharacterSheetPage />
                      </Layout>
                    }
                  />
                }
              />
              
              {/* Profile Route */}
              <Route 
                path="/profile" 
                element={
                  <ProtectedRoute 
                    element={
                      <Layout>
                        <Profile />
                      </Layout>
                    } 
                  />
                } 
              />

              {/* Settings Route */}
              <Route
                path="/settings"
                element={
                  <ProtectedRoute
                    element={
                      <Layout>
                        <Settings />
                      </Layout>
                    }
                  />
                }
              />
              
              {/* Admin Routes */}
              <Route 
                path="/admin" 
                element={
                  <ProtectedRoute 
                    element={
                      <Layout>
                        <RoleBasedAccess requiredRoles={[RoleType.ADMIN]}>
                          <AdminDashboard />
                        </RoleBasedAccess>
                      </Layout>
                    } 
                  />
                } 
              />
              
              {/* Dev Controls Route - Admin only */}
              <Route 
                path="/dev-controls" 
                element={
                  <ProtectedRoute 
                    element={
                      <Layout>
                        <RoleBasedAccess requiredRoles={[RoleType.ADMIN]}>
                          <DevControls />
                        </RoleBasedAccess>
                      </Layout>
                    } 
                  />
                } 
              />
              
              {/* =================================================================
                  PLANNED FEATURE ROUTES
                  These routes will show the themed 404/NotFound page with
                  "Coming Soon" messaging until the features are implemented.

                  INTEGRATION POINT: As features are completed:
                  1. Create the actual page component
                  2. Import it at the top of this file
                  3. Replace the NotFound element with the new component
                  ================================================================= */}
              <Route path="/session/new" element={<NotFound />} />
              <Route path="/character/new" element={<NotFound />} />
              <Route path="/notes" element={<NotFound />} />
              <Route path="/spellbook" element={<NotFound />} />
              <Route path="/map" element={<NotFound />} />
              <Route path="/voice" element={<VoicePage />} />

              {/* Fallback route - themed 404 page for unknown routes */}
              <Route path="*" element={<NotFound />} />
            </Routes>
            </RouteResetErrorBoundary>
          </Router>
        </CampaignProvider>
      </ThemeProvider>
    </AuthProvider>
  );
};

export default App;
